import React, { useState, useMemo, Suspense, lazy } from "react";
import { AdminOverview } from "./admin/AdminOverview";

// Heavy tab content — lazy split per tab keeps the admin shell tiny.
const AdminOrdersTable    = lazy(() => import("./admin/AdminOrdersTable").then(m => ({ default: m.AdminOrdersTable })));
const ProductManager      = lazy(() => import("./admin/ProductManager").then(m => ({ default: m.ProductManager })));
const ReviewsModeration   = lazy(() => import("./admin/ReviewsModeration").then(m => ({ default: m.ReviewsModeration })));
const AuthDryRunPanel     = lazy(() => import("./AuthDryRunPanel").then(m => ({ default: m.AuthDryRunPanel })));
const AdminPaymentsQueue  = lazy(() => import("./payments/AdminPaymentsQueue").then(m => ({ default: m.AdminPaymentsQueue })));
const AdminPaymentsMetrics = lazy(() => import("./payments/AdminPaymentsMetrics").then(m => ({ default: m.AdminPaymentsMetrics })));
const PaymentSettingsEditor = lazy(() => import("./payments/PaymentSettingsEditor").then(m => ({ default: m.PaymentSettingsEditor })));
const WebhooksManager       = lazy(() => import("./admin/WebhooksManager").then(m => ({ default: m.WebhooksManager })));
const AzulTransactionsAdmin = lazy(() => import("./admin/AzulTransactionsAdmin").then(m => ({ default: m.AzulTransactionsAdmin })));
const CrossSellDashboard    = lazy(() => import("./admin/CrossSellDashboard").then(m => ({ default: m.CrossSellDashboard })));
const ShippingTestPanel     = lazy(() => import("./admin/ShippingTestPanel").then(m => ({ default: m.ShippingTestPanel })));
const HeroEditor            = lazy(() => import("./admin/HeroEditor").then(m => ({ default: m.HeroEditor })));
const OffersManager         = lazy(() => import("./admin/OffersManager").then(m => ({ default: m.OffersManager })));
const PushCampaignsManager  = lazy(() => import("./admin/PushCampaignsManager").then(m => ({ default: m.PushCampaignsManager })));
const BundlesManager        = lazy(() => import("./admin/BundlesManager").then(m => ({ default: m.BundlesManager })));
const AdminPayoutsManager   = lazy(() => import("./admin/AdminPayoutsManager").then(m => ({ default: m.AdminPayoutsManager })));
const AdminReplenishment    = lazy(() => import("./admin/AdminReplenishment").then(m => ({ default: m.AdminReplenishment })));
const AdminHealthPanel      = lazy(() => import("./admin/AdminHealthPanel").then(m => ({ default: m.AdminHealthPanel })));
const AdminMarketingAutomation = lazy(() => import("./admin/AdminMarketingAutomation").then(m => ({ default: m.AdminMarketingAutomation })));
const AdminAnalyticsDashboard = lazy(() => import("./admin/AdminAnalyticsDashboard").then(m => ({ default: m.AdminAnalyticsDashboard })));
const AdminAlertsSettings = lazy(() => import("./admin/AdminAlertsSettings").then(m => ({ default: m.AdminAlertsSettings })));
const AdminBackupsPanel = lazy(() => import("./admin/AdminBackupsPanel").then(m => ({ default: m.AdminBackupsPanel })));
const AdminAuditLog = lazy(() => import("./admin/AdminAuditLog").then(m => ({ default: m.AdminAuditLog })));
const QAModeration = lazy(() => import("./admin/QAModeration").then(m => ({ default: m.QAModeration })));
const AdminAffiliates = lazy(() => import("./admin/AdminAffiliates").then(m => ({ default: m.AdminAffiliates })));
const AdminAiBundles = lazy(() => import("./admin/AdminAiBundles").then(m => ({ default: m.AdminAiBundles })));
const AdminWinBack = lazy(() => import("./admin/AdminWinBack").then(m => ({ default: m.AdminWinBack })));
const AdminVendorApplications = lazy(() => import("./admin/AdminVendorApplications").then(m => ({ default: m.AdminVendorApplications })));

const TabFallback = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 30, color: "var(--tx3)" }}>
    <div style={{ width: 22, height: 22, border: "2px solid var(--bd2)", borderTopColor: "var(--ac)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
  </div>
);

const TAB_IDS = ["orders", "payments", "products", "replenish", "hero", "reviews", "qa", "webhooks", "crosssell", "shiptest", "deals", "bundles", "aibundles", "pushcamp", "winback", "marketing", "analytics", "alerts", "backups", "audit", "payouts", "affiliates", "vendorapps", "health", "users", "diagnostics"] as const;

export function AdminPanel({ adminOrders, adminTab, setAdminTab, adminTrackingInputs, setAdminTrackingInputs, adminSetTracking, adminCreateShipment, adminAdvanceStatus, P, lang, t, mapStatus, $, user, toast }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filteredOrders = useMemo(() => {
    return adminOrders.filter(o => {
      if (dateFrom && o.date < dateFrom) return false;
      if (dateTo && o.date > dateTo) return false;
      return true;
    });
  }, [adminOrders, dateFrom, dateTo]);

  // CSV exports — kept here because they depend on local filteredOrders / P.
  const exportOrdersCSV = () => {
    const headers = ["ID", "Customer", "Total", "Status", "Date", "Tracking"];
    const rows = filteredOrders.map(o => [o.shortId, `"${o.cust}"`, o.total, o.status, o.date, o.tracking || ""]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    triggerDownload(csv, `nutrimarket-orders-${new Date().toISOString().split("T")[0]}.csv`);
  };
  const exportProductsCSV = () => {
    const headers = ["Product", "Brand", "SKU", "Price", "Stock", "Category"];
    const rows = P.map(p => [
      `"${typeof p.nm === "object" ? (p.nm[lang] || p.nm.en) : p.nm}"`,
      `"${p.br || ""}"`,
      p.sku || "",
      p.pr,
      p.stk ?? 0,
      `"${p.cat || ""}"`
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    triggerDownload(csv, `nutrimarket-products-${new Date().toISOString().split("T")[0]}.csv`);
  };
  const onExportCSV = adminTab === "products" ? exportProductsCSV : exportOrdersCSV;

  return (
    <>
      <div className="ph">
        <h2>{t.adminDash}</h2>
        <p>{t.manageMarket}</p>
      </div>

      <AdminOverview
        adminOrders={adminOrders}
        filteredOrders={filteredOrders}
        P={P} lang={lang} t={t} $={$} mapStatus={mapStatus}
        dateFrom={dateFrom} dateTo={dateTo}
        setDateFrom={setDateFrom} setDateTo={setDateTo}
        onExportCSV={onExportCSV}
      />

      {/* Tabs */}
      <div className="atabs">
        {TAB_IDS.map(tb => (
          <button key={tb} className={`atab ${adminTab === tb ? "on" : ""}`} onClick={() => setAdminTab(tb)}>
            {tb === "reviews" ? (lang === "es" ? "Reseñas" : "Reviews")
              : tb === "qa" ? (lang === "es" ? "Q&A" : "Q&A")
              : tb === "crosssell" ? (lang === "es" ? "Cross-sell" : "Cross-sell")
              : tb === "shiptest" ? (lang === "es" ? "Test envíos" : "Shipping test")
              : tb === "hero" ? (lang === "es" ? "Hero / Banner" : "Hero / Banner")
            : tb === "pushcamp" ? (lang === "es" ? "Push campañas" : "Push campaigns")
              : tb === "bundles" ? (lang === "es" ? "Paquetes" : "Bundles")
              : tb === "aibundles" ? (lang === "es" ? "Bundles AI" : "AI Bundles")
              : tb === "winback" ? (lang === "es" ? "Win-back" : "Win-back")
              : tb === "payouts" ? (lang === "es" ? "Payouts" : "Payouts")
              : tb === "affiliates" ? (lang === "es" ? "Afiliados" : "Affiliates")
              : tb === "vendorapps" ? (lang === "es" ? "Vendedores" : "Vendor apps")
              : tb === "replenish" ? (lang === "es" ? "Reposición" : "Replenishment")
              : tb === "health" ? (lang === "es" ? "Salud" : "Health")
              : tb === "marketing" ? (lang === "es" ? "Marketing" : "Marketing")
              : tb === "analytics" ? (lang === "es" ? "Analítica" : "Analytics")
              : tb === "alerts" ? (lang === "es" ? "Alertas" : "Alerts")
              : tb === "backups" ? (lang === "es" ? "Backups" : "Backups")
              : tb === "audit" ? (lang === "es" ? "Auditoría" : "Audit log")
              : (t[tb] || tb)}
          </button>
        ))}
      </div>

      <Suspense fallback={<TabFallback />}>
        {adminTab === "diagnostics" && <AuthDryRunPanel lang={lang} />}

        {adminTab === "payments" && (
          <>
            <AdminPaymentsMetrics lang={lang} />
            <PaymentSettingsEditor lang={lang} />
            <AdminPaymentsQueue lang={lang} />
            <AzulTransactionsAdmin lang={lang} />
          </>
        )}

        {adminTab === "orders" && (
          <AdminOrdersTable
            filteredOrders={filteredOrders}
            products={P}
            lang={lang} t={t} $={$} mapStatus={mapStatus}
            adminTrackingInputs={adminTrackingInputs}
            setAdminTrackingInputs={setAdminTrackingInputs}
            adminSetTracking={adminSetTracking}
            adminCreateShipment={adminCreateShipment}
            adminAdvanceStatus={adminAdvanceStatus}
          />
        )}

        {adminTab === "products" && (
          <ProductManager mode="admin" user={user} lang={lang} t={t} $={$} toast={toast} />
        )}

        {adminTab === "hero" && (
          <HeroEditor P={P} lang={lang} toast={toast} />
        )}

        {adminTab === "reviews" && <ReviewsModeration lang={lang} />}

        {adminTab === "qa" && <QAModeration lang={lang} toast={toast} />}

        {adminTab === "webhooks" && <WebhooksManager lang={lang} />}

        {adminTab === "crosssell" && <CrossSellDashboard lang={lang} />}

        {adminTab === "shiptest" && <ShippingTestPanel lang={lang} />}

        {adminTab === "deals" && <OffersManager lang={lang} toast={toast} />}

        {adminTab === "pushcamp" && <PushCampaignsManager lang={lang} />}

        {adminTab === "bundles" && <BundlesManager lang={lang} toast={toast} />}

        {adminTab === "aibundles" && <AdminAiBundles lang={lang} toast={toast} />}

        {adminTab === "winback" && <AdminWinBack lang={lang} toast={toast} />}

        {adminTab === "vendorapps" && <AdminVendorApplications lang={lang} toast={toast} />}

        {adminTab === "payouts" && <AdminPayoutsManager lang={lang} $={$} />}

        {adminTab === "affiliates" && <AdminAffiliates lang={lang} $={$} toast={toast} />}

        {adminTab === "replenish" && <AdminReplenishment lang={lang} toast={toast} />}

        {adminTab === "health" && <AdminHealthPanel lang={lang} toast={toast} />}

        {adminTab === "marketing" && <AdminMarketingAutomation lang={lang} toast={toast} />}

        {adminTab === "analytics" && <AdminAnalyticsDashboard lang={lang} toast={toast} />}

        {adminTab === "alerts" && <AdminAlertsSettings lang={lang} toast={toast} />}

        {adminTab === "backups" && <AdminBackupsPanel lang={lang} toast={toast} />}

        {adminTab === "audit" && <AdminAuditLog lang={lang} toast={toast} />}
      </Suspense>
    </>
  );
}

function triggerDownload(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
